<script setup lang="ts">
import ElCurrencyInput from '@/components/common/ElCurrencyInput.vue'
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import { withUndoNotification } from '@/components/rc/common/RcWithUndoNotification'
import { useModelFamilyPricings } from '@/composables/asicw/pricings'
import { CoolingMethod, Environment, type IPolicyMinerEntry } from '@/types/asicw/policies'
import type { IMinerFamilyPricingFilters, IMinerModelFamilyPricing } from '@/types/asicw/pricings'
import { Icon } from '@iconify/vue'
import type { ElForm } from 'element-plus'
import _, { cloneDeep, last } from 'lodash'
import { vMaska } from 'maska/vue'
import { computed, h, reactive, ref, toRaw } from 'vue'

const dropdownTriangle = h(Icon, { icon: 'mdi:triangle-small-down' })
const duplicateSNs = ref<string[]>([]) // Duplicate SNs

const modelFamilyPricingFilters = reactive<IMinerFamilyPricingFilters>({
  page: 1,
  per_page: 1000,
})
const { modelFamilyPricingsQuery } = useModelFamilyPricings()
const {
  data: modelFamilyPricingOptions,
  isError,
  error,
  isFetching,
} = modelFamilyPricingsQuery(modelFamilyPricingFilters)
const availableModelFamilyOptions = computed(() =>
  modelFamilyPricingOptions.value?.data.map((pricing: IMinerModelFamilyPricing) => {
    return {
      value: pricing.miner_model_family,
      label: pricing.miner_model_family,
    }
  })
)

const form = defineModel<IPolicyMinerEntry[]>({ required: true })

const defaultMiner: IPolicyMinerEntry = {
  sn: '',
  policySalePrice: 0,
  environment: Environment.ModularDataCenter,
  coolingMethod: CoolingMethod.AirCooled,
  running3rdPartySoft: false,
  overclockingMiners: false,
  number: 0,
}

const addMiner = () => {
  if (form.value.length >= 100) return
  const lastMiner = last(form.value)

  // Push the default one
  if (!lastMiner) {
    form.value.push(structuredClone(defaultMiner))
    return
  }

  // Clone over the last one in the list
  form.value.push({
    ...structuredClone(toRaw(lastMiner)),
    sn: '',
    model_family: undefined,
    policySalePrice: 0,
    number: lastMiner.number + 1,
  })
}

const isFormValid = computed(
  () => duplicateSNs.value.length == 0 && form.value.every((miner) => !!miner.sn && !!miner.model_family)
)

const verifyDuplicates = () => {
  const sns = form.value.map((miner) => miner.sn)
  duplicateSNs.value = _(sns)
    .filter() // Filters empties
    .groupBy() // Groups by value
    .pickBy((x) => x.length > 1)
    .keys()
    .value()
}

const formRef = ref()

const minerSnFormRules = [
  {
    validator: (rule: unknown, value: IPolicyMinerEntry['sn'], callback: (err?: Error) => void) => {
      if (duplicateSNs.value.includes(value)) {
        callback(new Error('Duplicate SN'))
      } else {
        callback()
      }
    },
    trigger: [], // Purposely leave this out as it's run for _all_ miners with SN from the SN input's @blur event
  },
  {
    required: true,
    message: 'Please input serial number',
    trigger: ['blur'],
  },
]

const minerModelFamilyFormRules = [
  {
    required: true,
    message: 'Please input model family',
    trigger: [], // Trigger from select's @blur as default behavior isn't working
  },
]

/**
 * Runs field validators for miners with SN set (to flag duplicates)
 */
const validatePopulatedSNs = () => {
  verifyDuplicates()
  form.value
    .filter((miner) => !!miner.sn)
    .forEach((miner) => formRef.value.validateField(`${miner.number.toString()}.sn`))
}

const deleteMiner = (number: number) => {
  const backupMiners = cloneDeep(form.value)
  form.value = form.value.filter((miner) => miner.number != number).map((miner, index) => ({ ...miner, number: index }))
  withUndoNotification({
    text: 'Miner deleted',
    onUndo: async () => {
      form.value = backupMiners
    },
  })
}

const determineMinerSalePrice = (miner: IPolicyMinerEntry) => {
  if (!miner.model_family) return

  // Find pricing based on the miner model family
  const pricing = modelFamilyPricingOptions.value?.data.find((p) => p.miner_model_family == miner.model_family)
  if (!pricing) return

  let salePrice = pricing.wholesale_base_cost

  switch (miner.environment) {
    case Environment.ModularDataCenter:
      salePrice += pricing.modular_facility_type_adder
      break
    case Environment.StandaloneBuilding:
      salePrice += pricing.traditional_facility_type_adder
      break
  }

  switch (miner.coolingMethod) {
    case CoolingMethod.AirCooled:
      salePrice += pricing.air_cooling_type_adder
      break
    case CoolingMethod.Immersion:
      salePrice += pricing.immersion_cooling_type_adder
      break
    case CoolingMethod.Water:
      salePrice += pricing.water_cooling_type_adder
      break
  }

  if (miner.running3rdPartySoft) {
    salePrice += pricing.third_party_firmware_adder
  }

  if (miner.overclockingMiners) {
    salePrice += pricing.overclocking_adder
  }

  miner.policySalePrice = salePrice
}

defineExpose({ isFormValid })
</script>

<template>
  <el-form ref="formRef" class="w-full px-6" :model="form">
    <!-- Error state -->
    <rl-error-alert v-if="isError" :error="error" />

    <el-scrollbar height="460px">
      <!-- Loading state -->
      <template v-if="isFetching">
        <el-skeleton v-for="miner in form" :key="miner.number" :animated="true" class="py-4">
          <template #template>
            <div class="grid grid-cols-2 gap-2 p-2">
              <el-skeleton-item v-for="i in 10" :key="i" class="w-full h-6" />
            </div>
          </template>
        </el-skeleton>
      </template>

      <template v-else>
        <!-- Validate the whole form on each miner's blur event, so duplicates get properly highlighted -->
        <div class="flex flex-col gap-2">
          <el-form-item v-for="miner in form" :key="miner.number" :prop="`${miner.number.toString()}`">
            <div class="group w-full flex flex-col border-b-1 border-b-[var(--component-stroke-primary)] pb-4 mr-4">
              <div class="flex gap-2 items-center w-full">
                <p class="whitespace-nowrap text-text-component-label">Miner #{{ miner.number + 1 }}</p>
                <Icon
                  icon="mdi:trash-can-outline"
                  :height="20"
                  class="cursor-pointer color-text-component-label shrink-0 ml-auto invisible group-hover:visible"
                  @click="deleteMiner(miner.number)"
                />
              </div>
              <div class="w-full grid grid-cols-2 gap-2 p-2">
                <el-form-item :prop="`${miner.number.toString()}.model_family`" :rules="minerModelFamilyFormRules">
                  <el-select-v2
                    v-if="availableModelFamilyOptions"
                    v-model="miner.model_family"
                    :options="availableModelFamilyOptions"
                    placeholder="Select model family"
                    value-key="miner_model_family"
                    filterable
                    @blur="formRef.validateField(`${miner.number.toString()}.model_family`)"
                    @change="determineMinerSalePrice(miner)"
                  />
                </el-form-item>
                <el-form-item :prop="`${miner.number.toString()}.sn`" :rules="minerSnFormRules">
                  <el-input
                    v-model="miner.sn"
                    v-maska
                    data-maska="X#"
                    data-maska-tokens="X:[A-Za-z0-9]:multiple"
                    placeholder="Serial Number"
                    @input="verifyDuplicates"
                    @blur="validatePopulatedSNs"
                  />
                </el-form-item>
                <div class="flex items-center gap-2">
                  <el-text class="text-text-tertiary">Sale price</el-text>
                  <el-tooltip
                    class="box-item"
                    effect="dark"
                    content="Price is determined by the miner's model family, environment and miner characteristics"
                    placement="top"
                  >
                    <Icon icon="material-symbols:help-outline" height="16" />>
                  </el-tooltip>
                </div>

                <ElCurrencyInput v-model="miner.policySalePrice" class="lightweight" />
                <el-text class="text-text-tertiary">Environment</el-text>
                <el-select
                  v-model="miner.environment"
                  class="lightweight"
                  :suffix-icon="dropdownTriangle"
                  @change="determineMinerSalePrice(miner)"
                >
                  <el-option :value="Environment.ModularDataCenter" :label="Environment.ModularDataCenter">{{
                    Environment.ModularDataCenter
                  }}</el-option>
                  <el-option :value="Environment.StandaloneBuilding" :label="Environment.StandaloneBuilding">{{
                    Environment.StandaloneBuilding
                  }}</el-option>
                </el-select>
                <el-text class="text-text-tertiary">Cooling method</el-text>
                <el-select
                  v-model="miner.coolingMethod"
                  class="lightweight"
                  :suffix-icon="dropdownTriangle"
                  @change="determineMinerSalePrice(miner)"
                >
                  <el-option :value="CoolingMethod.AirCooled" :label="CoolingMethod.AirCooled" />
                  <el-option :value="CoolingMethod.Immersion" :label="CoolingMethod.Immersion" />
                  <el-option :value="CoolingMethod.Water" :label="CoolingMethod.Water" />
                </el-select>
                <el-text class="text-text-tertiary">3rd party software</el-text>
                <el-select
                  v-model="miner.running3rdPartySoft"
                  class="lightweight"
                  :suffix-icon="dropdownTriangle"
                  @change="determineMinerSalePrice(miner)"
                >
                  <el-option :value="true" label="Yes">Yes</el-option>
                  <el-option :value="false" label="No">No</el-option>
                </el-select>
                <el-text class="text-text-tertiary">Overclocking miners</el-text>
                <el-select
                  v-model="miner.overclockingMiners"
                  class="lightweight"
                  :suffix-icon="dropdownTriangle"
                  @change="determineMinerSalePrice(miner)"
                >
                  <el-option :value="true" label="Yes">Yes</el-option>
                  <el-option :value="false" label="No">No</el-option>
                </el-select>
              </div>
            </div>
          </el-form-item>
          <el-link class="text-text-brand self-start" @click="addMiner">+ Add a miner</el-link>
        </div>
      </template>
    </el-scrollbar>
    <p v-if="form.length == 100" class="text-xs py-2 text-text-tertiary">
      You have reached the maximum number of miners allowed for a single bulk-create operation
    </p>
  </el-form>
</template>

<style scoped lang="scss">
:deep() {
  .lightweight {
    .el-select {
      &__wrapper {
        background-color: transparent;
        box-shadow: none; // Surprise! Select's border is not border
      }

      &__selection {
        flex: none;
      }

      &__selected-item,
      &__placeholder {
        position: relative;
        transform: none;

        span {
          color: var(--text-secondary);
        }
      }

      &__input-wrapper {
        position: absolute;
      }
    }
  }
}
</style>
